import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { ManualEntryForm } from "@/components/journal/ManualEntryForm";
import { useActiveCompany } from "@/lib/company-context";
import { useCreateManualEntry } from "@/lib/api/journal.queries";
import { ApiError } from "@/lib/api";
import type { CreateManualEntryInput } from "@/lib/api/journal";

export const Route = createFileRoute(
  "/companies/$companyId/contabilidad/diario/nuevo",
)({
  component: NewManualEntryPage,
});

function NewManualEntryPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewManualEntryContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewManualEntryContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const navigate = useNavigate();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const { mutateAsync, isPending } = useCreateManualEntry(companyId);

  async function handleSubmit(input: CreateManualEntryInput) {
    try {
      const entry = await mutateAsync(input);
      toast.success(t("journal.success.posted"));
      void navigate({
        to: "/companies/$companyId/contabilidad/diario/$entryId",
        params: { companyId, entryId: entry.id },
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : t("journal.error.postFailed");
      toast.error(msg);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            void navigate({
              to: "/companies/$companyId/contabilidad/diario",
              params: { companyId },
            })
          }
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">{t("journal.actions.newManualEntry")}</h1>
      </div>
      <ManualEntryForm
        companyId={companyId}
        onSubmit={handleSubmit}
        isSubmitting={isPending}
      />
    </div>
  );
}
